/**
 * Shared FloorPlan opening parser — single source for 2D + 3D renderers.
 *
 * The typed FloorPlan node carries `doorOpenings` and `windowOpenings`
 * as JSON-encoded string arrays (so the ontology field type stays
 * `string` and travels through schema.org JSON-LD round-trips). This
 * helper parses + validates them once.
 *
 * Promoted here from lib/floorplanSvg.ts so Scene3D.tsx can read the
 * same openings the 2D plan reads — same source of truth for door
 * positions, window glazing, and any future opening kinds (vents,
 * pass-throughs).
 */
import type { GraphNode } from "../types/ontology";

export type WallSide = "top" | "right" | "bottom" | "left";

export interface Opening {
  /** Which of the 4 walls this opening sits on. */
  readonly wall: WallSide;
  /** Position along the wall, 0..1 from the canonical start corner. */
  readonly start: number;
  /** Opening width in feet (same unit as FloorPlan.widthFt / depthFt). */
  readonly width: number;
}

function isWallSide(x: unknown): x is WallSide {
  return x === "top" || x === "right" || x === "bottom" || x === "left";
}

export function parseOpenings(raw: string | undefined | null): Opening[] {
  if (!raw) return [];
  try {
    const arr = JSON.parse(raw) as unknown;
    if (!Array.isArray(arr)) return [];
    return arr.flatMap((o): Opening[] => {
      if (!o || typeof o !== "object") return [];
      const obj = o as Record<string, unknown>;
      if (!isWallSide(obj["wall"])) return [];
      const start = typeof obj["start"] === "number" ? obj["start"] : -1;
      const width = typeof obj["width"] === "number" ? obj["width"] : -1;
      if (start < 0 || start > 1 || width <= 0) return [];
      return [{ wall: obj["wall"], start, width }];
    });
  } catch (err) {
    // CLAUDE.md §5 — surface, never silently swallow.
    // eslint-disable-next-line no-console
    console.error("[openings.parseOpenings] invalid JSON in opening field", err);
    return [];
  }
}

/** Convenience: read both door + window openings from a FloorPlan node. */
export function openingsFromPlan(plan: GraphNode | undefined): { doors: Opening[]; windows: Opening[] } {
  if (!plan) return { doors: [], windows: [] };
  const door = plan.attrs["doorOpenings"];
  const win = plan.attrs["windowOpenings"];
  return {
    doors: parseOpenings(typeof door === "string" ? door : null),
    windows: parseOpenings(typeof win === "string" ? win : null),
  };
}
